import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Smile, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { PRACTICE } from '../data/practice';

/**
 * Lightweight fixed header for inner pages (about, pricing, contact, services, areas, 404).
 * Includes a tap-to-open mobile menu for navigation on phones.
 */
const NAV_LINKS = [
  { to: '/iv-sedation-dentistry', label: 'IV Sedation' },
  { to: '/pricing', label: 'Prices' },
  { to: '/about-dr-kartik-patel', label: 'Dr Patel' },
  { to: '/contact', label: 'Contact' },
];

const PageHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sky-gray/20 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2" aria-label="Restore.Dental home" onClick={() => setMenuOpen(false)}>
            <Smile className="w-8 h-8 text-burnt-coral" aria-hidden="true" />
            <span className="text-xl md:text-2xl font-bold text-rabbit">restore.dental</span>
          </Link>

          <nav className="flex items-center gap-2 md:gap-4" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="hidden md:inline text-sm font-medium text-rabbit hover:text-burnt-coral"
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}-link`}
              >
                {l.label}
              </Link>
            ))}
            <a href={PRACTICE.phoneTel} className="hidden sm:flex items-center gap-2 text-burnt-coral hover:text-sky-gray transition-colors" data-testid="header-phone-link">
              <Phone className="w-5 h-5" aria-hidden="true" />
              <span className="font-semibold">{PRACTICE.phoneDisplay}</span>
            </a>
            <Button asChild size="sm" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white">
              <Link to="/book" data-testid="header-book-link">Book Now</Link>
            </Button>

            {/* Mobile menu toggle */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center w-10 h-10 -mr-2 text-rabbit hover:text-burnt-coral transition-colors"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav-panel"
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </nav>
        </div>
      </div>

      {/* Mobile dropdown panel */}
      {menuOpen && (
        <div
          id="mobile-nav-panel"
          className="md:hidden border-t border-sky-gray/20 bg-white/98 backdrop-blur-sm shadow-lg animate-in slide-in-from-top-2 duration-200"
          data-testid="mobile-nav-panel"
        >
          <nav className="container mx-auto px-4 py-3 flex flex-col" aria-label="Mobile">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="py-3 text-base font-medium text-rabbit hover:text-burnt-coral border-b border-sky-gray/10 last:border-b-0"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}-link`}
              >
                {l.label}
              </Link>
            ))}
            <a
              href={PRACTICE.phoneTel}
              onClick={() => setMenuOpen(false)}
              className="py-3 flex items-center gap-2 text-base font-semibold text-burnt-coral hover:text-sky-gray"
              data-testid="mobile-nav-phone-link"
            >
              <Phone className="w-5 h-5" aria-hidden="true" />
              Call {PRACTICE.phoneDisplay}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default PageHeader;
